import * as React from 'react';
import type { Editor } from '@tiptap/react';

import type { CommentThreadSummary } from './comment-threads';

/** Nothing anchored, nothing to measure. */
const NO_OFFSETS: Record<string, number> = {};

/**
 * Where each thread's first line sits vertically, measured from the top of `ref`.
 *
 * Measured rather than derived: a comment's anchor is a range of text whose position
 * moves with every edit above it, every reflow and every font that finishes loading,
 * and ProseMirror is the only thing that knows where a document position ended up on
 * screen.
 *
 * `ref` is whatever element the caller positions against. Its own `scrollTop` is
 * subtracted, which is what lets the same measurement serve a layer sitting inside the
 * scrolled content — where it is always zero — and one anchored to the scroll
 * container itself, where it is not.
 *
 * Two things keep this off the hot path. `coordsAtPos` forces layout, once per thread,
 * and the editor fires `update` on every keystroke, so the high-frequency callers are
 * coalesced into one measurement per animation frame: twenty keystrokes in a frame
 * cost one pass, not twenty. And the inputs are read through a ref, so the listener
 * and the observer are attached once for the life of the editor instead of being torn
 * down and re-attached every time a thread's text changes.
 *
 * The first measurement is still synchronous, in a layout effect, so cards and chips
 * paint in the right place rather than jumping there a frame later.
 */
export function useCommentAnchorOffsets(
  editor: Editor | null,
  threads: CommentThreadSummary[],
  ref: React.RefObject<HTMLElement | null>
): Record<string, number> {
  const [offsets, setOffsets] =
    React.useState<Record<string, number>>(NO_OFFSETS);
  const frame = React.useRef<number | null>(null);
  const latest = React.useRef({ editor, threads, ref });

  React.useEffect(() => {
    latest.current = { editor, threads, ref };
  });

  const measure = React.useCallback(() => {
    const {
      editor: currentEditor,
      threads: currentThreads,
      ref: currentRef,
    } = latest.current;
    const element = currentRef.current;
    if (
      element === null ||
      currentEditor === null ||
      currentEditor.isDestroyed
    ) {
      return;
    }
    if (currentThreads.length === 0) {
      setOffsets((previous) =>
        Object.keys(previous).length === 0 ? previous : NO_OFFSETS
      );
      return;
    }

    const base = element.getBoundingClientRect().top - element.scrollTop;
    const next: Record<string, number> = {};
    for (const thread of currentThreads) {
      // A position can be outside the document for the moment between an edit landing
      // and this running again; there is simply nowhere to put anything.
      if (thread.anchorFrom >= currentEditor.state.doc.content.size) {
        continue;
      }
      try {
        next[thread.commentId] =
          currentEditor.view.coordsAtPos(thread.anchorFrom).top - base;
      } catch {
        continue;
      }
    }
    setOffsets((previous) => {
      const keys = Object.keys(next);
      const unchanged =
        keys.length === Object.keys(previous).length &&
        keys.every((key) => previous[key] === next[key]);
      // Bailing on an equal result is what keeps the resize observer below from
      // feeding itself.
      return unchanged ? previous : next;
    });
  }, []);

  const schedule = React.useCallback(() => {
    if (frame.current !== null) {
      return;
    }
    frame.current = requestAnimationFrame(() => {
      frame.current = null;
      measure();
    });
  }, [measure]);

  React.useEffect(
    () => () => {
      if (frame.current !== null) {
        cancelAnimationFrame(frame.current);
        frame.current = null;
      }
    },
    []
  );

  // Synchronous, so the first paint is in the right place. Re-runs when the set of
  // threads changes, which is rare next to the two sources below.
  React.useLayoutEffect(() => {
    measure();
  }, [editor, measure, threads]);

  React.useEffect(() => {
    if (editor === null) {
      return;
    }
    // Every edit reflows the text under these, including edits far above them that
    // change no thread at all.
    editor.on('update', schedule);
    return () => {
      editor.off('update', schedule);
    };
  }, [editor, schedule]);

  React.useEffect(() => {
    const element = ref.current;
    if (element === null) {
      return;
    }
    const observer = new ResizeObserver(schedule);
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref, schedule]);

  return offsets;
}
